import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
// Video
import SampleVideo720 from '../../assets/video/commercial-sample-video-siv-renate-720.mp4';
import SampleVideo1080 from '../../assets/video/commercial-sample-video-siv-renate-1080.mp4';
import Poster from '../../assets/images/posters/commercial-sample-video-siv-renate-poster.webp';
// Constants
import { CompanyName } from '../../utils/Constants';
import { BOOKING_PAGE_URL } from '../../utils/Routes';
// Icons
import { MdDoubleArrow } from 'react-icons/md';
import { FaClock, FaRegCommentDots } from 'react-icons/fa';

function CommercialInfoContainer() {
  const inlineVideoRef = useRef(null);

  useEffect(() => {
    const v = inlineVideoRef.current;
    if (!v) return;

    const tryPlay = () => {
      const p = v.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    // kick playback on load
    const onReady = () => tryPlay();
    v.addEventListener('canplay', onReady, { once: true });
    v.addEventListener('loadeddata', onReady, { once: true });

    // pause/resume based on viewport
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) tryPlay();
          else v.pause();
        });
      },
      { rootMargin: '100px 0px 100px 0px', threshold: 0.2 }
    );
    io.observe(v);

    // pause if tab hidden
    const onVis = () => {
      if (document.hidden) {
        v.pause();
        return;
      }
      const r = v.getBoundingClientRect();
      const inView =
        r.bottom >= 1 &&
        r.right >= 1 &&
        r.top <= (window.innerHeight || document.documentElement.clientHeight) &&
        r.left <= (window.innerWidth || document.documentElement.clientWidth);
      if (inView) tryPlay();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      v.removeEventListener('canplay', onReady);
      v.removeEventListener('loadeddata', onReady);
      io.disconnect();
    };
  }, []);

  return (
    <section className='grid gap-6 lg:gap-y-12 px-8 py-8 xl:py-24 2xl:px-32 lg:px-16'>
      {/* Heading */}
      <div className='grid gap-y-4 lg:gap-y-6 border-b-2 border-solid border-colour2 dark:border-colour3 mx-auto pb-2'>
        <h3 className='font-titleFont text-2xl lg:text-3xl text-center'>
          COMMERCIALS & UGC CREATION
        </h3>
        <h4 className='text-center lg:text-xl italic font-serif'>
          by {CompanyName}
        </h4>
      </div>

      {/* Intro row */}
      <div className='grid lg:grid-cols-2 gap-8 dark:text-colour2'>
        {/* Text */}
        <section className='grid bg-colour1 rounded-3xl shadow-lg p-8 place-items-center'>
          <article className='grid gap-6 text-center'>
            <h2 className='text-3xl font-bold font-titleFont'>
              Commercial Video Production
            </h2>
            <p className='text-base opacity-90'>
              Professional video content has the power to capture attention,
              showcase your brand, and leave a lasting impression. I create{' '}
              <span className='font-semibold'>commercials</span> and{' '}
              <span className='font-semibold'>UGC videos</span> that balance
              creativity with clarity — tailored to engage your target audience
              and highlight what makes your brand unique.
            </p>
            <p className='text-base opacity-90'>
              From scripting and storyboarding to filming and editing, every
              step is designed to deliver polished, high-quality visuals that
              reflect your message and meet your goals. Whether you need a
              full-scale brand campaign or quick, impactful UGC, I can help
              bring your vision to life.
            </p>
            <div className='grid px-4 justify-center'>
              <Link
                to={BOOKING_PAGE_URL}
                className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
                aria-label={`Book a commercial project with ${CompanyName}`}
                title={`Book a commercial project with ${CompanyName}`}
                rel='bookmark'
              >
                <div className='grid grid-cols-rev items-center border border-colour1 border-solid gap-x-8 py-1 px-4 lg:py-2 lg:px-8'>
                  <span>BOOK YOUR PROJECT</span>
                  <MdDoubleArrow className='text-xl lg:text-2xl' />
                </div>
              </Link>
            </div>
          </article>
        </section>

        {/* Supporting info */}
        <section className='grid bg-blue-100 text-colour2 rounded-3xl shadow-lg p-8 place-items-center'>
          <article className='grid gap-4 text-center'>
            <h2 className='text-2xl font-bold font-titleFont'>
              Why Choose {CompanyName}?
            </h2>
            <p className='text-base opacity-90'>
              Every video is more than just visuals — it’s an experience. My
              approach combines creative direction with technical expertise,
              ensuring your project feels authentic, professional, and
              results-driven.
            </p>
            <p className='text-base opacity-90'>
              I pride myself on being easy to work with, responsive to your
              needs, and committed to delivering content that reflects your
              brand’s personality. Together, we’ll create commercials and UGC
              that don’t just look great but also perform.
            </p>
          </article>
        </section>
      </div>

      {/* Features + inline video */}
      <div className='grid lg:grid-cols-3 gap-8'>
        <aside className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6 text-center place-items-center'>
          <FaClock className='text-4xl text-colour5' aria-hidden='true' />
          <h3 className='text-xl font-semibold font-titleFont'>Timely Delivery</h3>
          <span className='text-sm italic text-colour5 opacity-80'>
            Always on schedule
          </span>
          <p className='text-sm opacity-80'>
            Deadlines matter — I deliver high-quality work quickly without
            cutting corners.
          </p>
        </aside>

        <aside className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6 text-center place-items-center'>
          <FaRegCommentDots className='text-4xl text-colour5' aria-hidden='true' />
          <h3 className='text-xl font-semibold font-titleFont'>
            Collaborative Process
          </h3>
          <span className='text-sm italic text-colour5 opacity-80'>
            Your voice matters
          </span>
          <p className='text-sm opacity-80'>
            Open communication and feedback at every step to ensure your vision
            is fully realized.
          </p>
        </aside>

        <aside className='grid rounded-3xl overflow-hidden shadow-lg'>
          <video
            ref={inlineVideoRef}
            autoPlay
            loop
            muted
            playsInline
            preload='auto'
            poster={Poster}
            disablePictureInPicture
            controlsList='nodownload noremoteplayback nofullscreen'
            className='w-full h-full object-cover'
          >
            <source src={SampleVideo720} type='video/mp4' />
            <source src={SampleVideo1080} type='video/mp4' media='(min-width: 1024px)' />
            Your browser does not support the video tag.
          </video>
        </aside>
      </div>
    </section>
  );
}

export default CommercialInfoContainer;
